import { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { UserCheck, Phone, Mail, Store, Clock, Filter } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  new: { label: 'Nouveau', variant: 'default', className: 'bg-blue-100 text-blue-800' },
  contacted: { label: 'Contacte', variant: 'secondary', className: 'bg-yellow-100 text-yellow-800' },
  converted: { label: 'Converti', variant: 'default', className: 'bg-emerald-100 text-emerald-800' },
  lost: { label: 'Perdu', variant: 'destructive', className: 'bg-red-100 text-red-800' },
};

function LeadStatusBadge({ status, onStatusChange, leadId }) {
  const [open, setOpen] = useState(false);
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.new;

  const handleChange = async (newStatus) => {
    setOpen(false);
    await onStatusChange(leadId, newStatus);
  };

  return (
    <div className="relative">
      <button
        data-testid={`lead-status-${leadId}`}
        onClick={() => setOpen(!open)}
        className={`px-3 py-1 rounded-full text-xs font-semibold cursor-pointer ${config.className}`}
      >
        {config.label}
      </button>
      {open && (
        <div className="absolute right-0 top-8 z-20 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[140px]">
          {Object.entries(STATUS_CONFIG).map(([key, val]) => (
            <button
              key={key}
              onClick={() => handleChange(key)}
              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${key === status ? 'font-bold' : ''}`}
            >
              <span className={`inline-block w-2 h-2 rounded-full mr-2 ${val.className.split(' ')[0]}`} />
              {val.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminLeads() {
  useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchLeads = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/leads/admin/all`);
      setLeads(response.data.leads || []);
    } catch {
      toast.error('Erreur lors du chargement des leads');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await axios.patch(
        `${API}/leads/admin/${leadId}/status?status=${newStatus}`,
        {}
      );
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: newStatus } : l));
      toast.success('Statut mis a jour');
    } catch {
      toast.error('Erreur lors de la mise a jour');
    }
  };

  const filtered = statusFilter === 'all' ? leads : leads.filter(l => l.status === statusFilter);

  const stats = {
    total: leads.length,
    new: leads.filter(l => !l.status || l.status === 'new').length,
    contacted: leads.filter(l => l.status === 'contacted').length,
    converted: leads.filter(l => l.status === 'converted').length,
  };

  if (loading) {
    return (
      <AdminLayout title="Leads Partenaires">
        <div className="grid sm:grid-cols-4 gap-4 mb-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-24 bg-gray-200 rounded-2xl animate-pulse" />
          ))}
        </div>
        <div className="h-96 bg-gray-200 rounded-2xl animate-pulse" />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Leads Partenaires">
      {/* Stats */}
      <div data-testid="leads-stats" className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <UserCheck className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-sm text-gray-500">Total leads</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
              <Clock className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.new}</p>
              <p className="text-sm text-gray-500">Nouveaux</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
              <Phone className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.contacted}</p>
              <p className="text-sm text-gray-500">Contactes</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <Store className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.converted}</p>
              <p className="text-sm text-gray-500">Convertis</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3 mb-4">
        <Filter className="w-4 h-4 text-gray-400" />
        <div className="flex gap-2">
          {[
            { key: 'all', label: 'Tous' },
            { key: 'new', label: 'Nouveaux' },
            { key: 'contacted', label: 'Contactes' },
            { key: 'converted', label: 'Convertis' },
            { key: 'lost', label: 'Perdus' },
          ].map(f => (
            <button
              key={f.key}
              data-testid={`filter-${f.key}`}
              onClick={() => setStatusFilter(f.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === f.key
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {filtered.length} lead{filtered.length !== 1 ? 's' : ''}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <div data-testid="no-leads" className="text-center py-12 text-gray-500">
              <UserCheck className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>Aucun lead pour le moment</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table data-testid="leads-table" className="w-full">
                <thead>
                  <tr className="border-b text-left text-sm text-gray-500">
                    <th className="pb-3 font-medium">Nom</th>
                    <th className="pb-3 font-medium">Email</th>
                    <th className="pb-3 font-medium">Telephone</th>
                    <th className="pb-3 font-medium">Commerce</th>
                    <th className="pb-3 font-medium">Date</th>
                    <th className="pb-3 font-medium">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((lead) => (
                    <tr key={lead.id} data-testid={`lead-row-${lead.id}`} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="py-3 font-medium text-gray-900">{lead.name}</td>
                      <td className="py-3">
                        <a href={`mailto:${lead.email}`} className="text-emerald-600 hover:underline flex items-center gap-1">
                          <Mail className="w-3.5 h-3.5" />
                          {lead.email}
                        </a>
                      </td>
                      <td className="py-3 text-gray-600">
                        {lead.phone ? (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3.5 h-3.5" />
                            {lead.phone}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="py-3">
                        <Badge variant="outline">{lead.trade || '-'}</Badge>
                      </td>
                      <td className="py-3 text-sm text-gray-500">
                        {new Date(lead.created_at).toLocaleDateString('fr-FR', {
                          day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                      </td>
                      <td className="py-3">
                        <LeadStatusBadge
                          status={lead.status || 'new'}
                          leadId={lead.id}
                          onStatusChange={handleStatusChange}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
